/* eslint-disable @typescript-eslint/ban-ts-comment */
import localforage from 'localforage';

const localStore = localforage.createInstance({ name: 'localStore' });
const cacheStore = localforage.createInstance({ name: 'cacheStore' });

/**
 * API caching strategy based on params;
 * If no params are passed it defaults to Network Only strategy.
 * Else it uses a Cache First strategy.

 Network Only -> Network fetch only without cache (Default)
 Cache First -> Get cache with valid expiry or fetch network otherwise

 * @param {object} params
      - cache: (Required) human readable duration of cache
      - persist: (Optional) if true, data will stay in cache until it expires (it will not be cleared at signOut)
      - id: (Optional) Id to store this api call in the database (defaults to: logged email) best if caching no Auth APIs

 * @example
 * cacheApi({...rest }) -> no cache
 * cacheApi({...rest, params: { cache: '10w' }}) -> caches for 10 weeks (or until signOut)
 * cacheApi({...rest, params: { cache: '5m' }}) -> caches for 5 minutes (or until signOut)
 * cacheApi({...rest, params: { cache: '3d', persist: true }}) -> caches for 3 full days (ignores signOut)
 */
export const cacheApi = async ({
  path,
  options,
  responseHandler,
  params = {},
}) => {
  // if no cache params it defaults to a Network only strategy
  // @ts-ignore
  if (!params.cache) {
    const res = await fetch(path, options);
    return await responseHandler(res);
  }

  let payload = null;
  // Create a unique key with current logged user and the api's path
  // @ts-ignore
  const email = params.id || sessionStorage.getItem('user');
  let key = typeof path === 'object' ? path.href : path;
  key = `${email}-${key}`;

  // Get cached data
  // @ts-ignore
  payload = await getItem(key, params);
  if (payload) return payload;

  // Fetch Network if data is empty
  const res = await fetch(path, options);
  payload = await responseHandler(res);
  // Only cache successful payloads
  if (res.status >= 200 && res.status <= 299) {
    // @ts-ignore
    await setItem(key, payload, params);
  }

  return payload;
};

/**
 * Easily set future time expiries using human readable strings
 *
 * @param {string} metric - human readable lapse of time
 * @param {number} timestamp - (optional) set expiry to timestamp, defaults to Date.now()
 * @example
 * setExpiry('1y') // 1 year
 * setExpiry('3w') // 3 weeks
 * setExpiry('2d') // 2 days
 * setExpiry('1h') // 1 hour
 * setExpiry('20m') // 20 minutes
 * setExpiry('10s') // 10 seconds
 * @returns {number}
 */
// switch statements are allowed to avoid complexity rule
 
export function setExpiry(metric: string, timestamp?: number): number {
  const ts = timestamp || Math.floor(Date.now() / 1000);
  const interval = Number(metric.substring(0, metric.length - 1));
  const time = metric.substring(metric.length - 1);
  if (!interval)
    throw new Error("Metric's interval should be equal or greater than 1");

  switch (time) {
    case 's':
      return ts + Math.floor(interval);
    case 'm':
      return ts + Math.floor(interval * 60);
    case 'h':
      return ts + Math.floor(interval * 3600);
    case 'd':
      return ts + Math.floor(interval * 86400);
    case 'w':
      return ts + Math.floor(interval * (86400 * 7));
    case 'y':
      return ts + Math.floor(interval * 31557600);
    default:
      throw new Error(
        "Missing time parameter: 's' | 'm' | 'h' | 'd' | 'w' | 'y' ",
      );
  }
}

const setItem = async (key, item, { cache, persist }) => {
  const store = persist ? localStore : cacheStore;
  // @ts-ignore
  const data = { item, exp: setExpiry(cache) };
  return await store.setItem(key, data);
};

const getItem = async (key: string, { persist }) => {
  const store = persist ? localStore : cacheStore;
  const data = await store.getItem(key);
  // @ts-ignore
  if (data && isExpired(data.exp)) {
    await store.removeItem(key);
    return null;
  }

  // @ts-ignore
  return data?.item;
};

export const clearCache = () => {
  localforage.dropInstance({ name: 'cacheStore' });
};

export function isExpired(expiry: number) {
  return expiry < Math.floor(Date.now() / 1000);
}
